import { useApp } from '../lib/AppContext';
import { useAdmin } from '../lib/AdminContext';
import LyricPill from '../components/LyricPill';
import Button from '../components/Button';
import CivicCard from '../components/CivicCard';
import LoadingScreen from '../components/LoadingScreen';
import { useState, useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import Logo from '../assets/Untitled design (1).svg';
import { getCurrentWeekForApp, WeeklyCardData } from '../lib/weeklyCardsPlanner';

export default function Home() {
  const {
    navigateTo,
    getFeaturedCard,
  } = useApp();

  const { previewIncludeDrafts } = useAdmin();

  const [scrollY, setScrollY] = useState(0);
  const [heroHeight, setHeroHeight] = useState(520);
  const [isMobile, setIsMobile] = useState(false);
  const [weeklyCards, setWeeklyCards] = useState<WeeklyCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [currentWeekId, setCurrentWeekId] = useState<string | null>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const featuredCard = getFeaturedCard();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    async function loadWeeklyCards() {
      setLoading(true);
      const weekData = await getCurrentWeekForApp();
      console.log('[Home] Weekly cards data:', weekData);
      console.log('[Home] Cards array:', weekData?.cards);
      setWeeklyCards(weekData?.cards || []);
      setCurrentWeekId(weekData?.week?.id || null);
      setLoading(false);
    }
    loadWeeklyCards();
  }, [previewIncludeDrafts]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (heroRef.current) {
      setHeroHeight(heroRef.current.offsetHeight);
    }
  }, [featuredCard]);

  const MOBILE_FADE_START = heroHeight * 0.65;
  const MOBILE_FADE_END = heroHeight * 0.95;
  const MOBILE_STICKY_FADE_START = heroHeight * 0.75;
  const MOBILE_STICKY_FADE_END = heroHeight * 0.95;
  const DESKTOP_TRANSITION_START = heroHeight * 0.75;
  const DESKTOP_TRANSITION_END = heroHeight * 0.85;
  const DESKTOP_STICKY_FADE_START = heroHeight * 0.70;
  const DESKTOP_STICKY_FADE_END = heroHeight * 0.85;

  const heroCollapsed = isMobile
    ? scrollY > MOBILE_STICKY_FADE_END
    : scrollY > DESKTOP_STICKY_FADE_END;

  let heroOpacity = 1;
  if (!isMobile && scrollY > DESKTOP_TRANSITION_START) {
    const fadeProgress = (scrollY - DESKTOP_TRANSITION_START) / (DESKTOP_TRANSITION_END - DESKTOP_TRANSITION_START);
    heroOpacity = Math.max(0, 1 - fadeProgress);
  } else if (isMobile && scrollY > MOBILE_FADE_START) {
    const fadeProgress = (scrollY - MOBILE_FADE_START) / (MOBILE_FADE_END - MOBILE_FADE_START);
    heroOpacity = Math.max(0, 1 - fadeProgress);
  }

  let stickyBarOpacity = 0;
  if (isMobile && scrollY > MOBILE_STICKY_FADE_START) {
    const fadeProgress = (scrollY - MOBILE_STICKY_FADE_START) / (MOBILE_STICKY_FADE_END - MOBILE_STICKY_FADE_START);
    stickyBarOpacity = Math.min(1, fadeProgress);
  } else if (!isMobile && scrollY > DESKTOP_STICKY_FADE_START) {
    const fadeProgress = (scrollY - DESKTOP_STICKY_FADE_START) / (DESKTOP_STICKY_FADE_END - DESKTOP_STICKY_FADE_START);
    stickyBarOpacity = Math.min(1, fadeProgress);
  }

  const handleCardClick = (cardId: string) => {
    navigateTo('card-detail', cardId);
  };

  const handleStickyUnpack = () => {
    if (isMobile && heroRef.current) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        if (featuredCard) {
          handleCardClick(featuredCard.id);
        }
      }, 400);
    } else {
      if (featuredCard) {
        handleCardClick(featuredCard.id);
      }
    }
  };

  const getHeroSubline = (card: typeof featuredCard) => {
    if (!card) return 'In 30 seconds, understand what this song is trying to tell us.';
    return card.heroSubline
      ? `In 30 seconds, ${card.heroSubline}.`
      : 'In 30 seconds, understand what this song is trying to tell us.';
  };

  const artistWisdom = weeklyCards.find(c => c.card_type === 'artist_wisdom');
  const civicMoment = weeklyCards.find(c => c.card_type === 'civic_moment');
  const sparkThought = weeklyCards.find(c => c.card_type === 'spark_a_thought');

  console.log('[Home] weeklyCards:', weeklyCards);
  console.log('[Home] artistWisdom:', artistWisdom);
  console.log('[Home] civicMoment:', civicMoment);
  console.log('[Home] sparkThought:', sparkThought);

  if (!showContent) {
    return (
      <LoadingScreen
        shouldFadeOut={!loading}
        onFadeComplete={() => setShowContent(true)}
      />
    );
  }

  return (
    <div
      className="min-h-screen bg-[#111111]"
      style={{
        paddingBottom: 'calc(92px + env(safe-area-inset-bottom, 0px))'
      }}
    >
      {/* Sticky Header */}
      <div
        className="sticky top-0 z-30 transition-all duration-300 ease-out"
        style={{
          backgroundColor: heroCollapsed ? '#111111' : 'transparent',
          backdropFilter: heroCollapsed ? 'blur(12px)' : 'none',
          WebkitBackdropFilter: heroCollapsed ? 'blur(12px)' : 'none',
          borderBottom: heroCollapsed ? '1px solid var(--stroke-default)' : '1px solid transparent',
          boxShadow: heroCollapsed ? '0 10px 15px -3px rgba(0, 0, 0, 0.3)' : 'none',
          paddingTop: `calc(env(safe-area-inset-top, 0px) + ${heroCollapsed ? '0.75rem' : '1rem'})`,
          paddingBottom: heroCollapsed ? '0.75rem' : '1rem',
          minHeight: '60px',
        }}
      >
        {(heroCollapsed || stickyBarOpacity > 0) && featuredCard ? (
          <div
            className="px-6 flex items-center gap-3"
            style={{
              opacity: stickyBarOpacity,
              transform: `translateY(${(1 - stickyBarOpacity) * -10}px)`,
              transition: 'opacity 500ms ease-out, transform 500ms ease-out',
            }}
          >
            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 bg-brand-teal rounded-lg blur-md opacity-20"></div>
              <img
                src={featuredCard.albumArtUrl}
                alt={featuredCard.songTitle}
                className="relative w-10 h-10 rounded-lg object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/placeholder-civicbeat.svg';
                }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="section-label text-[10px] text-brand-teal">
                TODAY'S CIVICBEAT
              </p>
              <p className="text-[13px] text-text-primary font-medium truncate">
                {featuredCard.songTitle} · {featuredCard.artistName}
              </p>
            </div>
            <Button
              onClick={handleStickyUnpack}
              variant="primary"
              className="!h-[32px] !px-3.5 !text-[12px] !gap-3 flex-shrink-0"
              aria-label="Explore this song"
            >
              Explore
              <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        ) : null}
      </div>

      {/* Hero Section - Today's CivicBeat */}
      {featuredCard && (
        <div
          ref={heroRef}
          className="relative overflow-hidden -mt-[76px]"
          style={{
            opacity: heroOpacity,
            transform: isMobile ? `translateY(${(1 - heroOpacity) * -20}px)` : 'none',
            transition: 'opacity 600ms ease-in-out, transform 600ms ease-in-out',
            pointerEvents: heroOpacity < 0.3 ? 'none' : 'auto',
          }}
        >
          <div
            className="absolute inset-0 bg-cover bg-center blur-3xl opacity-20"
            style={{ backgroundImage: `url(${featuredCard.albumArtUrl})` }}
          ></div>

          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#111111]/70 to-[#111111]"></div>

          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#111111]" style={{
            background: 'radial-gradient(ellipse at center, transparent 20%, rgba(17,17,17,0.4) 70%, #111111 100%)'
          }}></div>

          <div className="relative px-6 pt-[70px] md:pt-[86px] pb-16">
            <div className="section-label text-[13px] text-brand-teal mb-10 text-center opacity-0 animate-hero-fade-in">
              TODAY'S CIVICBEAT
            </div>

            <div className="relative mb-10">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-[240px] h-[240px] bg-brand-teal rounded-full blur-3xl opacity-[0.025] animate-pulse-slow"></div>
              </div>

              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-[200px] h-[200px] bg-brand-teal rounded-full blur-2xl opacity-[0.03]"></div>
              </div>

              <img
                src={featuredCard.albumArtUrl}
                alt={featuredCard.songTitle}
                className="relative w-[180px] h-[180px] rounded-2xl mx-auto object-cover shadow-2xl"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/placeholder-civicbeat.svg';
                }}
              />
            </div>

            <div className="flex items-center justify-center gap-2 mb-3">
              <h2 className="text-[26px] font-bold text-text-primary text-center">
                {featuredCard.songTitle}
              </h2>
            </div>

            <p className="text-[15px] text-text-secondary text-center mb-8">
              {featuredCard.artistName}
            </p>

            {featuredCard.lyricHighlight && (
              <div className="mb-6 opacity-0 animate-hero-fade-in animate-hero-delay-1">
                <LyricPill lyricText={featuredCard.lyricHighlight} />
              </div>
            )}

            <p className="text-[13px] text-text-secondary text-center mb-8 px-2 opacity-0 animate-hero-fade-in animate-hero-delay-2" style={{ lineHeight: '1.55' }}>
              {getHeroSubline(featuredCard)}
            </p>

            <div className="w-full flex justify-center">
              <Button
                onClick={() => handleCardClick(featuredCard.id)}
                variant="primary"
                fullWidth
                className="max-w-md opacity-0 animate-hero-fade-in animate-hero-delay-3"
                aria-label="Explore This Song"
              >
                Explore This Song
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* CivicBeat Cards Section */}
      <div className="mt-8 md:mt-10 lg:mt-14 px-6 pb-8 md:pb-12 lg:pb-16">
        {/* Wrapper to center content on desktop */}
        <div className="md:max-w-[1128px] md:mx-auto">
          {/* Heading and subtitle - left-aligned on all breakpoints */}
          <div className="text-left">
            <h3 className="text-xl md:text-2xl font-semibold md:font-bold text-text-primary mb-0.5">
              The Beat Line
            </h3>
            <p className="text-sm md:text-base text-text-secondary mb-4">
              Small sparks where music meets the world.
            </p>
          </div>

          {/* Cards Container - Flexbox layout for iPhone-sized cards */}
          <div className="flex flex-col md:flex-row items-stretch md:items-start md:justify-center gap-5 md:gap-6">
            {/* Card 1 - Artist Wisdom */}
            <div className="w-full md:w-[360px] md:min-w-[360px] md:max-w-[360px] h-[480px]">
              {console.log('[Home] Rendering Artist Wisdom card with:', {
                content: artistWisdom?.content,
                attribution: artistWisdom?.attribution,
                image_url: artistWisdom?.image_url,
                fullObject: artistWisdom
              })}
              <CivicCard
                categoryLabel="Artist Wisdom"
                title={artistWisdom?.content || "Music is the voice of the voiceless"}
                subtitle={artistWisdom?.attribution ? `— ${artistWisdom.attribution}` : "A reminder that music can explain power better than textbooks."}
                meta="Origin: USA • Genre: Hip-hop"
                onClick={() => navigateTo('explore')}
                variant="wisdom"
                cardType="artist_wisdom"
                imageUrl={artistWisdom?.image_url || undefined}
                weekId={currentWeekId || undefined}
              />
            </div>

            {/* Card 2 - Civic Moment */}
            <div className="w-full md:w-[360px] md:min-w-[360px] md:max-w-[360px] h-[480px]">
              <CivicCard
                categoryLabel="Civic Moment"
                title={civicMoment?.headline || civicMoment?.content || "Music continues to move crowds"}
                subtitle={civicMoment?.content || "Music is still moving crowds, not just charts."}
                meta="Type: Protest • Impact: High"
                locationLabel={civicMoment?.country?.name || "Global · Movement"}
                onClick={() => navigateTo('explore')}
                variant="moment"
                cardType="civic_moment"
                imageUrl={civicMoment?.story_image_url || civicMoment?.image_url || civicMoment?.country?.image_url || undefined}
                countryCode={civicMoment?.country?.iso3 || undefined}
                eventDate={civicMoment?.event_date || undefined}
                sourceName={civicMoment?.source_name || undefined}
                sourceUrl={civicMoment?.source_url || undefined}
                headline={civicMoment?.headline || undefined}
                weekId={currentWeekId || undefined}
              />
            </div>

            {/* Card 3 - CivicSpark */}
            <div className="w-full md:w-[360px] md:min-w-[360px] md:max-w-[360px] h-[480px]">
              <CivicCard
                categoryLabel="CivicSpark"
                title={sparkThought?.content || "What did this week's song make you curious about?"}
                subtitle={sparkThought?.subtext || ""}
                meta="Focus: Reflection • Difficulty: Gentle"
                onClick={() => navigateTo('profile')}
                variant="spark"
                cardType="civic_spark"
                weekId={currentWeekId || undefined}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
